import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { trimFileNames } from '@/helpers/trimFileNames';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import {
  Box,
  Button,
  Modal,
  SelectInputNative,
  TextareaInput,
  toast,
  useBreakpoint,
} from '@palmetto/palmetto-components';
import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';

import { FlagDocumentTypes, FlagReason, UserPermissions } from 'types';

import { useUploadDocumentMutation } from '../../services/documents';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePermissions from '../auth/requirePermissions';

import { QueueFlagsContext } from './QueueFlagsContext';

interface AddFlagModalProps {
  accountId: string;
  isOpen: boolean;
  onDismiss: () => void;
  dataKey: string;
  fileName?: string;
  baseCategory?: string;
  showUploadButton?: boolean;
  type?: string;
}

export const AddFlagModal = ({
  accountId,
  isOpen,
  onDismiss,
  dataKey,
  fileName,
  baseCategory,
  showUploadButton = false,
  type,
}: AddFlagModalProps) => {
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const { addFlag, isLoading } = useContext(QueueFlagsContext);
  const [flagFiles, setFlagFiles] = useState<FileList | undefined>();
  const [uploadDocument] = useUploadDocumentMutation();
  const { isPhone } = useBreakpoint();

  const setNoteText = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value);
  }, []);
  const setReasonValue = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    setReason(event.target.value);
  }, []);
  const handleFileUpload = useCallback((event: any) => {
    setFlagFiles(event.target.files);
  }, []);
  const reasonOptions = useMemo(() => {
    return Object.values(FlagReason).map((value) => ({ value: value, label: value }));
  }, []);
  const allowsAction = useIsOrgReadOnly();

  const handleHide = useCallback(() => {
    setNote('');
    setReason('' as FlagReason);
    onDismiss();
    setFlagFiles(undefined);
  }, [onDismiss]);

  const handleSave = useCallback(async () => {
    if (isLoading) {
      return;
    }

    if (reason === '') {
      return toast.error('Flag Reason is required');
    }

    let uploadedDocumentId = '';

    try {
      if (flagFiles && flagFiles.length) {
        const data = new FormData();
        data.append('type', FlagDocumentTypes.flag);

        for (let i = 0; i < flagFiles.length; i++) {
          data.append(`files[${i}]`, flagFiles[i]);
        }

        const uploadedDocumentResult = (await uploadDocumentsWithToast({
          uploadDocument,
          accountId,
          formData: data,
          invalidateAllTags: false,
          files: flagFiles,
        })) as any;
        uploadedDocumentId = uploadedDocumentResult?.documents?.[0]?.id ?? '';
      }

      await addFlag({
        dataKey,
        fileName,
        note,
        reason: reason as FlagReason,
        baseCategory,
        fileUpload: uploadedDocumentId,
        type,
      });
      handleHide();
    } catch {
      // Upload or addFlag errored — toast is already shown by helper or callee.
    }
  }, [
    accountId,
    addFlag,
    dataKey,
    fileName,
    note,
    reason,
    baseCategory,
    flagFiles,
    uploadDocument,
    handleHide,
    isLoading,
    type,
  ]);

  const truncatedFileName = trimFileNames(fileName, dataKey);

  return (
    <Modal isOpen={isOpen} maxWidth="4xl" ariaLabelledBy="addFlagHeader" onDismiss={handleHide}>
      <Modal.Header id="addFlagHeader" title={`Flag ${truncatedFileName}`} onDismiss={handleHide} />
      <Modal.Body background="secondary" childGap="lg">
        <Box childGap="md">
          <SelectInputNative
            id="reason"
            label="Reason"
            value={reason}
            onChange={setReasonValue}
            options={reasonOptions}
            isRequired
          />
          <TextareaInput id="note" label="Notes (optional)" value={note} onChange={setNoteText} rows={3} />
          {showUploadButton && (
            <RequirePermissions
              permissions={[UserPermissions.admin, UserPermissions.editor]}
              checkAllPermissions={false}
            >
              <Box direction="row" childGap="md" alignItems="center">
                <ResettingFileUpload
                  labelText="Upload"
                  id={`upload-${FlagDocumentTypes.flag}-photos`}
                  name={`${FlagDocumentTypes.flag}-files`}
                  size="sm"
                  multiple
                  onChange={handleFileUpload}
                  buttonText={!isPhone ? 'Upload file(s)' : null}
                  onClick={(event: any) => event.stopPropagation()}
                  isDisabled={isLoading}
                  files={flagFiles}
                />
              </Box>
            </RequirePermissions>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button as="button" onClick={handleHide} variant="secondary" tone="neutral" size="md" disabled={isLoading}>
          Cancel
        </Button>
        <Button size="md" type="submit" onClick={handleSave} color="danger" disabled={isLoading || !allowsAction}>
          Flag Item
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
